import { useEffect, useState } from 'react';
import { Check, Copy, Download } from 'lucide-react';
import { toast } from 'sonner';
import { publicApiBase } from '@/shared/lib/passbook-utils';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';

/**
 * QR do link do cartão, no mesmo padrão de "Meu QR-Code" (/conta/qr-code):
 * o PNG personalizado da loja quando a API tem um (`/qrcode-personalizado/<id>.png`);
 * senão, QR padrão do link pelo qrserver. Abaixo, baixar e copiar o link.
 */
export function PassbookQr({
  idPassbook,
  url,
  name,
  className,
}: {
  idPassbook: string;
  url: string;
  /** Nome do arquivo baixado, sem extensão. */
  name: string;
  className?: string;
}) {
  const [hasCustomQr, setHasCustomQr] = useState<boolean | null>(null);
  const [copied, setCopied] = useState(false);
  const customQrUrl = `${publicApiBase()}/qrcode-personalizado/${idPassbook}.png`;
  const generatedQr = `https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=8&data=${encodeURIComponent(url)}`;

  useEffect(() => {
    let active = true;
    setHasCustomQr(null);
    fetch(customQrUrl, { method: 'HEAD' })
      .then((r) => {
        if (active) setHasCustomQr(r.ok);
      })
      .catch(() => {
        if (active) setHasCustomQr(false);
      });
    return () => {
      active = false;
    };
  }, [customQrUrl]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copiado!', { duration: 1600 });
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error('Não foi possível copiar.');
    }
  };

  return (
    <div className={cn('flex flex-col items-center gap-3', className)}>
      {hasCustomQr === null ? (
        <Skeleton className="size-56 rounded-xl" />
      ) : (
        <img
          src={hasCustomQr ? customQrUrl : generatedQr}
          alt={`QR Code de ${url}`}
          width={224}
          height={224}
          className="size-56 rounded-xl bg-white p-2 ring-1 ring-black/5"
        />
      )}
      <div className="flex items-center gap-2">
        {hasCustomQr === null ? (
          <Button type="button" variant="secondary" size="sm" disabled>
            <Download /> Baixar
          </Button>
        ) : hasCustomQr ? (
          // PNG de outro host: o `download` é ignorado pelo navegador, então abre em outra aba.
          <Button variant="secondary" size="sm" asChild>
            <a href={customQrUrl} download={`${name}.png`} target="_blank" rel="noreferrer">
              <Download /> Baixar
            </a>
          </Button>
        ) : (
          <Button variant="secondary" size="sm" asChild>
            <a href={`${generatedQr.replace('size=280x280', 'size=300x300')}&download=1`} download={`${name}.png`}>
              <Download /> Baixar
            </a>
          </Button>
        )}
        <Button type="button" variant="secondary" size="sm" onClick={() => void copy()} aria-label="Copiar link do cartão">
          {copied ? <Check className="text-success" /> : <Copy />} Copiar link
        </Button>
      </div>
    </div>
  );
}
